import xhr from 'xhr';
import settings from 'settings';

/**
 * Simulador de la Ventanilla (fase S). El equipo escribe como si fuera un estudiante en WhatsApp y
 * responde el mismo agente que atenderá en HiBot. Endpoints en klazia-api: src/api/ventanilla.
 * Todo queda marcado como prueba: canal «simulador», PDF con marca y sin correos.
 */

export const SIM_ROLES = ['admin', 'supervisor_admin', 'secretary_admin'];

export const CONVERSATION_STATUS = {
  activa: { label: 'Activa', tone: 'green' },
  transferida: { label: 'Pasó a una persona', tone: 'blue' },
  cerrada: { label: 'Cerrada', tone: 'gray' },
};

export const TOOL_LABEL = {
  buscar_estudiante: 'Buscar estudiante',
  verificar_identidad: 'Verificar identidad',
  revisar_constancia: 'Revisar constancia',
  crear_solicitud: 'Crear solicitud',
  vista_previa: 'Vista previa',
  generar_link_pago: 'Generar cobro',
  emitir_y_enviar: 'Emitir y enviar',
  pasar_a_persona: 'Pasar a una persona',
  consultar_estado: 'Consultar estado',
};

/** Los PDF subidos en local llegan con ruta relativa (/uploads/...). */
export const fileUrl = (url) => (url && url.startsWith('/') ? `${settings.api}${url}` : url);

export const PROVIDER_LABEL = { openai: 'OpenAI', openai_responses: 'OpenAI razonando', anthropic: 'Anthropic' };

/** Conversaciones anteriores, los modelos, los escenarios y los modos de cobro (cada conversación queda con los suyos). */
export async function listSimulations() {
  const { data } = await xhr.get('/ventanilla-sim/conversations');
  return { items: data?.data || [], providers: data?.providers || null, scenarios: data?.scenarios || [], paymentModes: data?.paymentModes || [] };
}

export async function createSimulation(body) {
  const { data } = await xhr.post('/ventanilla-sim/conversations', body);
  return data;
}

export async function getSimulation(id) {
  const { data } = await xhr.get(`/ventanilla-sim/conversations/${id}`);
  return data;
}

export async function updateSimulation(id, body) {
  const { data } = await xhr.put(`/ventanilla-sim/conversations/${id}`, body);
  return data;
}

export async function sendSimulationMessage(id, text) {
  const { data } = await xhr.post(`/ventanilla-sim/conversations/${id}/messages`, { text });
  return data;
}

export async function simulatePayment(id) {
  const { data } = await xhr.post(`/ventanilla-sim/conversations/${id}/pay`);
  return data;
}

/* Cobro con Wompi sandbox ----------------------------------------------------------------------- */

export const CHARGE_STATUS = {
  pendiente: { label: 'Pendiente', tone: 'orange' },
  aprobado: { label: 'Aprobado', tone: 'green' },
  rechazado: { label: 'Rechazado', tone: 'red' },
  anulado: { label: 'Anulado', tone: 'gray' },
  error: { label: 'Error', tone: 'red' },
  vencido: { label: 'Vencido', tone: 'gray' },
};

export const PAYMENT_MODE_HINT = {
  simulado: 'No hay pago: se confirma con «Simular pago».',
  sandbox: 'Wompi de pruebas, sin dinero. Tarjeta aprobada: 4242 4242 4242 4242; rechazada: 4111 1111 1111 1111 (cualquier fecha futura y CVC). Nequi aprobado: 3991111111.',
};

/** Wompi devuelve a la persona al admin con ?id=<transacción>: se consulta el pago y se aplica. */
export async function verifyPayment(transactionId) {
  const { data } = await xhr.post('/ventanilla-sim/payments/verify', { transactionId });
  return data;
}

export async function advanceSimulation(id, hours) {
  const { data } = await xhr.post(`/ventanilla-sim/conversations/${id}/advance`, { hours });
  return data;
}

/* Calificación y hallazgos de las pruebas internas ------------------------------------------- */

export const RATING = {
  correcta: { label: 'Sí', tone: 'green' },
  parcial: { label: 'Parcial', tone: 'orange' },
  incorrecta: { label: 'No', tone: 'red' },
};

export const FINDING_CATEGORY = {
  problema: 'Problema',
  respuesta_incorrecta: 'Respuesta incorrecta',
  sugerencia: 'Sugerencia',
};

export const FINDING_IMPACT = {
  bloquea: { label: 'Bloquea la solicitud', tone: 'red' },
  confunde: { label: 'Confunde', tone: 'orange' },
  menor: { label: 'Detalle menor', tone: 'gray' },
};

export const FINDING_PRIORITY = {
  p0: { label: 'P0 · Bloqueante', hint: 'Riesgo de datos, elegibilidad, privacidad o emisión indebida. Detiene el piloto.' },
  p1: { label: 'P1 · Alta', hint: 'Impide completar una solicitud válida. Se corrige antes de abrir a estudiantes.' },
  p2: { label: 'P2 · Media', hint: 'Confunde u omite algo, pero hay alternativa manual.' },
  p3: { label: 'P3 · Baja', hint: 'Estilo o redacción, sin impacto funcional.' },
};

export const FINDING_STATUS = {
  nuevo: { label: 'Nuevo', tone: 'blue' },
  en_correccion: { label: 'En corrección', tone: 'orange' },
  listo_reprobar: { label: 'Listo para reprobar', tone: 'purple' },
  validado: { label: 'Validado', tone: 'green' },
  descartado: { label: 'Descartado', tone: 'gray' },
};

export const OPEN_FINDING_STATUSES = ['nuevo', 'en_correccion', 'listo_reprobar'];

export async function rateSimulation(id, body) {
  const { data } = await xhr.post(`/ventanilla-sim/conversations/${id}/rating`, body);
  return data;
}

export async function reportFinding(id, body) {
  const { data } = await xhr.post(`/ventanilla-sim/conversations/${id}/findings`, body);
  return data;
}

export async function listFindings(params) {
  const { data } = await xhr.get('/ventanilla-sim/findings', { params });
  return data;
}

export async function updateFinding(id, body) {
  const { data } = await xhr.put(`/ventanilla-sim/findings/${id}`, body);
  return data;
}
